import { useQuery } from "@tanstack/react-query";
import {
  getMccInitialScoresFn,
  getMccStudentsFn,
  IInitialScoreItem,
} from "../api/unitLog";
import { startTransition, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import AttendeesWrapper from "./AttendeesWrapper";
import { useSocketListener } from "../hooks/useSocketListener";

const getLatestScore = (str: string | null) => {
  if (str) {
    const res = str.split("/");
    return res.pop();
  } else {
    return <span className="text-gray-500">&middot;</span>;
  }
};

function findAvg(scoresObj: Record<number, string> | null) {
  if (scoresObj) {
    const scoresArr = Object.values(scoresObj).join("/").split("/");
    const scoresSum = scoresArr.reduce((a, b) => Number(a) + Number(b), 0);
    const avg = scoresSum / scoresArr.length;
    return (
      <span
        className={`block ${avg < 0.65 ? "text-red-500" : ""} ${avg >= 0.85 ? "text-green-500" : ""
          } ${avg >= 0.65 && avg < 0.85 ? "text-amber-500" : ""}`}
      >
        {Math.round(avg * 100) / 100}
      </span>
    );
  } else {
    return <span className="block">-</span>;
  }
}

interface ISocketUpdate {
  user_id: number;
  practice_id: number;
  score: string | number | null;
}

export default function Attendees({
  mcc_id,
  isLive,
}: {
  mcc_id: number;
  isLive: boolean;
}) {
  const [scores, setScores] = useState<null | IInitialScoreItem[]>(null);

  const { data: students, isSuccess: studentFetchSuccess } = useQuery({
    queryFn: async () => getMccStudentsFn({ mcc_id }),
    queryKey: ["mccStudents", { mcc_id }],
    enabled: !!mcc_id,
  });

  const { data: initialScores } = useQuery({
    queryFn: async () => getMccInitialScoresFn({ mcc_id }),
    queryKey: ["mccStudentsInitialScores", { mcc_id }],
    enabled: studentFetchSuccess,
  });

  useEffect(() => {
    if (initialScores) {
      setScores(initialScores);
    }
  }, [initialScores]);

  const divARef = useRef<HTMLDivElement>(null);
  const divBRef = useRef<HTMLDivElement>(null);
  const isSyncingRef = useRef(false);

  const handleScroll = (source: "A" | "B") => {
    if (isSyncingRef.current) return;

    isSyncingRef.current = true;

    const targetScrollTop =
      source === "A" ? divARef.current?.scrollTop : divBRef.current?.scrollTop;

    if (source === "A" && divBRef.current) {
      divBRef.current.scrollTop = targetScrollTop || 0;
    } else if (source === "B" && divARef.current) {
      divARef.current.scrollTop = targetScrollTop || 0;
    }

    // Scroll işlemi bitince tekrar izin ver
    setTimeout(() => {
      isSyncingRef.current = false;
    }, 10); // kısa gecikme yeterli
  };

  useEffect(() => {
    const divA = divARef.current;
    const divB = divBRef.current;

    if (divA && divB) {
      const onScrollA = () => handleScroll("A");
      const onScrollB = () => handleScroll("B");

      divA.addEventListener("scroll", onScrollA);
      divB.addEventListener("scroll", onScrollB);

      return () => {
        divA.removeEventListener("scroll", onScrollA);
        divB.removeEventListener("scroll", onScrollB);
      };
    }
  }, []);

  let currentPart: number | null = null;

  useSocketListener("send_result_to_admin", (update: ISocketUpdate) => {
    if (isLive) {
      startTransition(() => {
        setScores((prev) => {
          if (!prev) return prev;

          return prev.map((item) => {
            if (item.practice_id !== update.practice_id) return item;

            const userId = update.user_id.toString();
            const newScoreStr =
              update.score || update.score === 0
                ? update.score.toString()
                : "-0.02";

            const previousScore = item.results?.[userId] || "";
            const updatedScore = previousScore
              ? `${previousScore}/${newScoreStr}`
              : newScoreStr;

            const updatedResults: Record<string, string> = {
              ...(item.results || {}),
              [userId]: updatedScore,
            };

            return {
              ...item,
              results: updatedResults,
            };
          });
        });
      });
    }
  });

  return (
    <AttendeesWrapper className="w-full absolute bottom-0 left-0 p-2 pt-2 bg-myOrange/20 z-50 overflow-auto">
      <div className="flex flex-col h-full">
        {/* <div className="pb-2 text-right">info</div> */}
        <div className="bg-black border border-white/30 flex-1 w-full rounded-2xl text-xs">
          <div className="flex w-full h-full overflow-hidden">
            {/* students */}
            <div
              className="student-list w-40 shrink-0 text-ellipsis overflow-auto whitespace-nowrap p-4 pb-12 pr-0"
              ref={divARef}
            >
              <div className="p-1 mt-4">Öğrenciler</div>
              {students?.map((s) => (
                <div className="border-t border-white/20 p-1" key={s.id}>
                  {s.username}, {s.id}
                </div>
              ))}
            </div>

            {/* scores */}
            <div
              className="flex-1 overflow-auto p-4 pb-12 pl-0 select-none"
              ref={divBRef}
            >
              <div className="flex text-center attendeesList">
                {scores?.map((practice, prOrder) => {
                  const cx =
                    currentPart === practice.unit_ml_part_id
                      ? "border-white/20"
                      : "border-white/70";
                  currentPart = practice.unit_ml_part_id;
                  return (
                    <div
                      key={practice.practice_id}
                      className={clsx("border-l", cx)}
                    >
                      <div className="p-1">
                        <div>
                          <a
                            href={`/alistirma/${practice.practice_id}`}
                            target="_blank"
                            className="underline underline-offset-2"
                          >
                            {practice.practice_id}
                          </a>
                        </div>
                        <div>{findAvg(scores?.[prOrder]?.results)}</div>
                      </div>
                      <div>
                        {students?.map((s) => (
                          <div
                            key={s.id + practice.practice_id}
                            className="p-1 border-t border-white/20"
                          >
                            {getLatestScore(
                              practice.results ? practice.results[s.id] : null
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AttendeesWrapper>
  );
}
